import React from 'react';
import BlogArticleSites from './BlogArticleSites';
import { BLOG_ARTICLE_STATUS } from '../../Pages/BlogArticlesAdmin/BlogArticlesAdmin.types';
const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
];
const pad = (value) => {
    return String(value).padStart(2, '0');
};
const formatDateTime = (value) => {
    const date = new Date(value);
    return `${pad(date.getDate())} ${MONTHS[date.getMonth()]} ${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
// go-live times are picked and stored in GMT, so they are shown in GMT
const formatGoLiveTime = (value, withYear) => {
    const date = new Date(value);
    const year = withYear ? ` ${date.getUTCFullYear()}` : '';
    return `${pad(date.getUTCDate())} ${MONTHS[date.getUTCMonth()]}${year}, ${pad(date.getUTCHours())}:00 GMT`;
};
const STATUS_STYLES = {
    [BLOG_ARTICLE_STATUS.published]: {
        pill: 'bg-green-100 text-green-800',
        marker: 'rounded-full bg-green-600'
    },
    [BLOG_ARTICLE_STATUS.draft]: {
        pill: 'bg-gray-100 text-gray-700',
        marker: 'rounded-full bg-gray-500'
    },
    [BLOG_ARTICLE_STATUS.paused]: {
        pill: 'bg-amber-100 text-amber-800',
        marker: 'rounded-[1px] bg-amber-600'
    },
    [BLOG_ARTICLE_STATUS.scheduled]: {
        pill: 'bg-blue-100 text-blue-800',
        marker: 'rounded-full bg-blue-600'
    },
    [BLOG_ARTICLE_STATUS.deleted]: {
        pill: 'bg-red-100 text-red-800',
        marker: 'rounded-full bg-red-600'
    }
};
const IconButton = ({ label, icon, onClick, disabled, className = 'text-gray-700 hover:bg-gray-100', iconFont = 'material-symbols-outlined' }) => {
    return (React.createElement("button", { type: "button", title: label, "aria-label": label, onClick: onClick, disabled: disabled, className: `inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md disabled:opacity-40 ${className}` },
        React.createElement("span", { className: `${iconFont} text-xl`, "aria-hidden": true }, icon)));
};
const BlogArticleListRow = ({ row, articleAdminUri, isOnEverySite, buildSiteUrl, actions, busy }) => {
    var _a, _b, _c, _d;
    const statusCode = row.blogArticleStatus.code;
    const statusStyle = (_a = STATUS_STYLES[statusCode]) !== null && _a !== void 0 ? _a : STATUS_STYLES[BLOG_ARTICLE_STATUS.draft];
    const isPublished = statusCode === BLOG_ARTICLE_STATUS.published;
    const isScheduled = statusCode === BLOG_ARTICLE_STATUS.scheduled;
    const isDeleted = statusCode === BLOG_ARTICLE_STATUS.deleted;
    const title = row.title || '(untitled)';
    return (React.createElement("tr", { className: "h-16 border-b border-gray-100 last:border-b-0 hover:bg-gray-50" },
        React.createElement("td", { className: "px-3 py-1 text-gray-500 tabular-nums" }, row.blogArticleId),
        React.createElement("td", { className: "px-3 py-1" },
            React.createElement("a", { href: articleAdminUri, title: title, className: "line-clamp-2 leading-[1.125rem] font-semibold text-gray-900 hover:underline" }, title),
            row.urlSlug && (React.createElement("div", { className: "truncate text-xs leading-4 text-gray-500", title: `/${row.urlSlug}` },
                "/",
                row.urlSlug))),
        React.createElement("td", { className: "truncate px-3 py-1", title: row.author }, row.author),
        React.createElement("td", { className: "truncate px-3 py-1", title: (_b = row.blogCategory) === null || _b === void 0 ? void 0 : _b.name }, (_d = (_c = row.blogCategory) === null || _c === void 0 ? void 0 : _c.name) !== null && _d !== void 0 ? _d : React.createElement("span", { className: "text-gray-400" }, "None")),
        React.createElement("td", { className: "overflow-hidden px-3 py-1" },
            React.createElement(BlogArticleSites, { blogArticleId: row.blogArticleId, siteCodes: row.sitesCodes, isOnEverySite: isOnEverySite, buildSiteUrl: (siteCode) => {
                    return buildSiteUrl(row, siteCode);
                } })),
        React.createElement("td", { className: "px-3 py-1" },
            React.createElement("span", { className: `inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap ${statusStyle.pill}` },
                React.createElement("span", { className: `h-1.5 w-1.5 ${statusStyle.marker}`, "aria-hidden": true }),
                row.blogArticleStatus.name),
            isScheduled && row.scheduledPublishAt && (React.createElement("div", { className: "mt-0.5 truncate text-xs text-gray-500", title: `Goes live ${formatGoLiveTime(row.scheduledPublishAt, true)}` }, formatGoLiveTime(row.scheduledPublishAt, false)))),
        React.createElement("td", { className: "px-3 py-1", title: `Created ${formatDateTime(row.createdAt)}` },
            React.createElement("div", { className: "whitespace-nowrap" }, formatDateTime(row.updated)),
            React.createElement("div", { className: "truncate text-xs text-gray-500" },
                "by ",
                row.updatedBy.name)),
        React.createElement("td", { className: "px-3 py-1" }, !isDeleted && (React.createElement("div", { className: "flex items-center gap-1" },
            React.createElement("button", { type: "button", onClick: () => {
                    actions.edit(row);
                }, disabled: busy, className: "mr-1 h-8 rounded-md border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-40" }, "Edit"),
            isPublished ? (React.createElement(IconButton, { label: "Pause", icon: "pause", iconFont: "material-icons", disabled: busy, onClick: () => {
                    actions.pause(row);
                } })) : (React.createElement(IconButton, { label: isScheduled ? 'Publish now' : 'Publish', icon: "play_arrow", disabled: busy, className: "text-green-700 hover:bg-green-50", onClick: () => {
                    actions.publish(row);
                } })),
            !isPublished && (React.createElement(IconButton, { label: isScheduled ? 'Reschedule' : 'Schedule', icon: isScheduled ? 'edit_calendar' : 'schedule', disabled: busy, onClick: () => {
                    actions.schedule(row);
                } })),
            isScheduled && (React.createElement(IconButton, { label: "Cancel schedule", icon: "event_busy", disabled: busy, onClick: () => {
                    actions.cancelSchedule(row);
                } })),
            React.createElement("span", { className: "ml-auto h-6 w-px shrink-0 bg-gray-300", "aria-hidden": true }),
            React.createElement(IconButton, { label: "Delete", icon: "delete", disabled: busy, className: "text-red-600 hover:bg-red-50", onClick: () => {
                    actions.remove(row);
                } }))))));
};
export default BlogArticleListRow;
